import React, { useEffect, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, submitFormData } from "../config/config";
import './Checkout_page.css';
import { Copy, CheckCircle } from 'lucide-react';

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [activeTab, setActiveTab] = useState({ name: '', network: '', address: '', id: '' });
  const [txHash, setTxHash] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Naya State: Order ID ko save karne ke liye
  const [generatedOrderId, setGeneratedOrderId] = useState('');

  const price = searchParams.get('price') || '0.00';
  const planName = searchParams.get('plan') || 'Document Service';
  const orderData = location.state?.orderData || {};
  const [copied, setCopied] = useState(false);


  // Component load hotay hi temporary ID generate kar rahe hain checkout page par dikhane ke liye
  useEffect(() => {
    const tempId = `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    setGeneratedOrderId(tempId);
    getPaymentMethods();
  }, []);

  const getPaymentMethods = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get-payment-methods`, {
        method: "GET",
        headers: { "Content-Type": "application/json" }
      });
      const result = await response.json();
      if (result.success == 1) {
        setPaymentMethods(result.payment_methods);
        setActiveTab(result.payment_methods[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

const handleFinalSubmit = async () => {
    setErrorMessage('');
    const cleanTx = txHash.trim();

    // 1. Validation: Check if empty
    if (!cleanTx) {
      setErrorMessage("Please enter Transaction Hash (TXID)");
      return;
    }

    // 2. Dynamic Validation based on Document 
    let isValid = false;
    let expectedFormat = "";

    const coinName = activeTab.name?.toUpperCase();

    // Bitcoin, Litecoin, Ripple, Bitcoin Cash uses ~64 chars [cite: 5, 10, 13, 15]
    if (["BTC", "BITCOIN", "LTC", "LITECOIN", "XRP", "RIPPLE", "BCH", "BITCOIN CASH"].includes(coinName)) {
      isValid = /^[a-fA-F0-9]{64}$/.test(cleanTx);
      expectedFormat = "64-character alphanumeric string";
    } 
    // Ethereum, BSC, Polkadot, Avalanche start with 0x (66 chars) [cite: 8, 20, 24, 30]
    else if (["ETH", "ETHEREUM", "BSC", "BINANCE SMART CHAIN", "DOT", "POLKADOT", "AVAX", "AVALANCHE"].includes(coinName)) {
      isValid = /^0x[a-fA-F0-9]{64}$/.test(cleanTx);
      expectedFormat = "66-character string starting with 0x";
    }
    // Cardano starts with a0 
    else if (["ADA", "CARDANO"].includes(coinName)) {
      isValid = /^a0[a-fA-F0-9]{62}$/.test(cleanTx);
      expectedFormat = "64 characters starting with 'a0'";
    }
    // Solana uses Base58 (typically 44 chars) 
    else if (["SOL", "SOLANA"].includes(coinName)) {
      isValid = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(cleanTx);
      expectedFormat = "44-character Base58 string";
    }
    // Default fallback if coin is unknown
    else {
      isValid = cleanTx.length >= 32; 
      expectedFormat = "a valid transaction hash";
    }

    if (!isValid) {
      setErrorMessage(`Invalid TXID for ${activeTab.name}! Expected ${expectedFormat}.`);
      return;
    }

    setLoading(true);
    // ... rest of your API call logic

    const finalPayload = {
      ...orderData,
      order_id: generatedOrderId,
      transaction_hash: txHash.trim(),
      payment_currency: activeTab.name,
      final_amount: price,
      status: 'pending',
      order_date: new Date().toISOString()
    };

    try {
      const res = await submitFormData(finalPayload);
      if (res && (res.success === 1 || res.status === "success" || res.success === true)) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(res.message || "Could not process order.");
      }
    } catch (error) {
      setErrorMessage("Server connection failed.");
    } finally {
      setLoading(false);
    }
  };

  // --- Success Page View ---
  if (isSubmitted) {
    return (
      <div className="checkout-container">
        <div className="thank-you-card" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <CheckCircle size={70} color="#16a34a" style={{ marginBottom: '20px' }} />
          <h1 style={{ fontSize: '2rem' }}>Your order is Pending we're confirming your payment!</h1>

          {/* Order Serial Number Highlight */}
          {/* <div style={{ background: '#f1f5f9', padding: '15px', borderRadius: '8px', margin: '20px auto', maxWidth: '450px', border: '1px dashed #cbd5e1' }}>
            <span style={{ color: '#64748b', fontSize: '14px' }}>Your Order Serial Number:</span>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#0f172a', marginTop: '5px' }}>{generatedOrderId}</div>
          </div> */}

          <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
            We've received your order {generatedOrderId} we'll check and confirm your payment <br />
            TXID: <code style={{ wordBreak: 'break-all' }}>{txHash}</code> <br />
            and we'll process your order immediately after that.
          </p>
          <button className="pay-btn" style={{ marginTop: '20px' }} onClick={() => navigate('/')}>Return to Home</button>
        </div>
      </div>
    );
  }

  // --- Checkout Page View ---
  return (
    <div className="checkout-container">
      <main className="checkout-main">
        <h1 className="checkout-title">Complete Your Order</h1>
        {/* Order ID shown on main checkout page */}
        {/* <p style={{ textAlign: 'center', color: '#64748b', marginTop: '-20px', marginBottom: '30px' }}>
          Order Reference: <strong>{generatedOrderId}</strong>
        </p> */}

        <div className="checkout-grid">
          <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="card-seting-sec1" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div className="card-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>

                {/* Upper Section: Title and Items */}
                <div className="top-section" style={{ flex: 1 }}>
                  <h3 style={{ marginBottom: '15px' }}>Order Summary</h3>
                  <div className="summary-item" style={{ borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                    {/* <h4 style={{ color: '#1e293b', fontSize: '18px' }}>{planName}</h4> */}
                    <div style={{ color: '#1e293b', fontSize: '18px' }}>{generatedOrderId}</div>
                    <div className="dynamic-info-list" style={{ marginTop: '12px' }}>
                      {Object.entries(orderData).map(([key, value]) => {
                        const skip = ['price', 'service_type', 'category_slug'];
                        if (skip.includes(key) || !value) return null;
                        let displayLabel = key === 'delivery_handle' ? 'Recipient' : key.replace(/_/g, ' ');
                        return (
                          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '5px' }}>
                            <span style={{ color: '#64748b', textTransform: 'capitalize' }}>{displayLabel}:</span>
                            <span style={{ color: '#0f172a', fontWeight: '500', textAlign: 'right', marginLeft: '10px' }}>{value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Bottom Section: Payable Amount (Hamesha niche rahega) */}
                <div className="checkout-page-set" style={{ marginTop: 'auto', paddingTop: '20px' }}>
                  <div className="total-section" style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>Payable Amount:</span>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>${price}</span>
                      </div>
                    </div>
                    <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '5px', textAlign: 'left' }}>
                      
                      Send that exact payment amount via : {activeTab.name}
                      <br />
                      Network: {activeTab.name} ({activeTab.network})
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className="card" style={{ padding: '30px' }}>
            <h3>Payment Method</h3>
            <div className="tabs">
              {paymentMethods.map((tab, index) => (
                <button key={index} className={`tab-btn ${activeTab.id === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                  {tab.name}
                </button>
              ))}
            </div>

            <div className="payment-body" style={{ marginTop: '25px' }}>
              <div className="address-box">
                <label className="label-text">{activeTab.name} Address</label>
                <div className="input-group">
                  <div className="address-input" style={{ fontFamily: 'monospace', fontSize: '11px' }}>{activeTab.address}</div>
                  {/* <button className="copy-btn" onClick={() => { navigator.clipboard.writeText(activeTab.address); alert("Copied!"); }}><Copy size={16} /></button> */}
                  {/* <button className="copy-btn" onClick={() => { navigator.clipboard.writeText(activeTab.address); }}  ><Copy size={25} /></button> */}
                  <button
  className={`copy-btn ${copied ? "active" : ""}`}
  onClick={() => {
    navigator.clipboard.writeText(activeTab.address);
    setCopied(true);

    // optional: 2 sec baad wapas normal ho jaye
    setTimeout(() => setCopied(false), 2000);
  }}
>
  <Copy size={25} />
</button>
                </div>
              </div>

              <div style={{ marginTop: '25px' }}>
                <label className="label-text">Transaction Hash (TXID)</label>
                <input
                  type="text"
                  className="address-input"
                  style={{ width: '100%', boxSizing: 'border-box', border: errorMessage ? '1px solid red' : '1px solid #cbd5e1' }}
                  value={txHash}
                  onChange={(e) => { setTxHash(e.target.value); setErrorMessage(''); }}
                  placeholder="Paste 64-character hash here"
                />
                {errorMessage && <p style={{ color: 'red', fontSize: '13px', marginTop: '5px' }}>{errorMessage}</p>}
              </div>

              <button className="pay-btn" style={{ marginTop: '30px', width: '100%' }} onClick={handleFinalSubmit} disabled={loading}>
                {loading ? "Processing..." : "I Have Paid"}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;